import type { ReactNode } from "react";
import { Select, MenuItem } from "@mui/material";
import { ChevronDown } from "../../icons/ChevronDown";
import '../../../assets/css/CustomSelect.css';

interface CustomSelectProps<T = any> {
  value: string | number;
  onChange: (event: any) => void;
  options: T[];
  placeholder?: string;
  minWidth?: string;
  heightOverride?: string;
  fontSizeOverride?: string;
  redOutline?: boolean;
  fullWidth?: boolean;
  aiToggle?: boolean;
  labelKey?: string;
  isStringArray?: boolean;
  disabled?: boolean;
  /** When set, replaces default text in each menu row (e.g. icon + label). */
  getOptionLabel?: (option: T) => ReactNode;
  /** When set, replaces the closed select’s label (e.g. icon + label). */
  getDisplayValue?: (selected: string | number) => ReactNode;
}

const CustomSingleSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  placeholder,
  minWidth,
  heightOverride,
  fontSizeOverride,
  labelKey,
  redOutline = false,
  fullWidth = false,
  isStringArray = false,
  disabled = false,
  getOptionLabel,
  getDisplayValue,
}) => {
    const renderFeatureValue = (selected: string | number) => {
        if (isStringArray) {
          return selected || placeholder;
        }
        const found = options.find((opt) => opt.id === selected);
        return found ? found[labelKey!] : placeholder;
      };

    if (!options || options.length === 0) {
      return null;
    }

  return (
      <Select
        className='custom-select'
        value={value || ""}
        onChange={onChange}
        displayEmpty
        IconComponent={ChevronDown}
        fullWidth={fullWidth}
        disabled={disabled}
        sx={{
          minWidth: minWidth ? minWidth : '0px',
          width: '100%',
          fontSize: fontSizeOverride || '12px',
          height: heightOverride || '32px',
          color: value != "" ? '#182737 !important' : "#8E8E93 !important",
          '& fieldset': {
            borderColor: redOutline ? '#BA2C2C' : '#C4C4C4',
            borderWidth: '1px',
          },
          '&:hover:not(.Mui-disabled) fieldset': {
            borderColor: redOutline ? '#BA2C2C' : '#212121 !important',
            borderWidth: '1px',
          },
          '&.Mui-focused fieldset': {
            border: redOutline ? '1px solid #BA2C2C' : '1px solid #245EA0',
          },
        }}
        inputProps={{ "aria-label": "Without label" }}
        renderValue={(selected) =>
          getDisplayValue
            ? getDisplayValue(selected as string | number)
            : `${renderFeatureValue(selected as string)}`
        }
        MenuProps={{
          MenuListProps: {
            className: 'custom-select-menu-list',
          },
        }}
      >
        {placeholder ? (
          <MenuItem sx={{ fontSize: fontSizeOverride || '12px' }} value="">
            {placeholder}
          </MenuItem>
        ) : null}
        {options?.map((option, index) => (
          <MenuItem 
            key={index} 
            value={isStringArray ? option : option.id} 
            sx={{ fontSize: fontSizeOverride || '12px' }} 
            disableRipple
          >
            {getOptionLabel
              ? getOptionLabel(option)
              : isStringArray
                ? option
                : option[labelKey!]}
          </MenuItem>
        ))}
      </Select>
  );
};

export { CustomSingleSelect };
